// Fix categories for app defects (main.app_defect) and fix proposals
// (main.app_fix_proposal). A category is the entire instruction surface for
// the appFixer agent: it decides which definition key paths a fix may touch.
//
// Two enforcement layers, both used by the run proxy and proposal submission:
// 1. Path rules: a diff path must not match a blocked prefix and must match
//    an allowed prefix for the defect's category.
// 2. Credential-flow freeze: the candidate definition's credential-flow
//    snapshot must equal the published version's byte for byte. This is the
//    backstop that catches value-level tricks path rules cannot see (for
//    example an outbound mapper whose location is 'headers').

// Credential-flow-critical and system-managed paths. Never fixable, in any
// category. Prefixes match normalized diff paths (array selectors stripped)
// on segment boundaries.
const APP_FIX_BLOCKED_PATH_PREFIXES = Object.freeze(['authentication', 'defaultAuthId', 'activeAppsScreenAuthId', 'authTypeCredentials', 'agentConnection', 'hooks', 'meta', 'version', 'semver', 'defaultVersion', 'updatedOn', 'whitelistedCompanies', 'api.baseUrl', 'api.headers', 'api.meta', 'actions.api.baseUrl', 'actions.api.type', 'actions.api.headers', 'actions.hooks', 'actions.headers', 'actions.meta', 'actions.oauth', 'actions.whitelistedCompanies']);

const appFixCategories = Object.freeze({
  pagination: Object.freeze({
    key: 'pagination',
    label: 'Pagination',
    description: 'Limit/offset/cursor traversal is missing, wrong, or clamped by the provider.',
    allowedPathPrefixes: Object.freeze(['actions.features.limit', 'actions.features.offset', 'api.features.limit', 'api.features.offset', 'api.mappers', 'actions.arguments'])
  }),
  filtering: Object.freeze({
    key: 'filtering',
    label: 'Filtering',
    description: 'Filter support is missing or does not match provider capabilities.',
    allowedPathPrefixes: Object.freeze(['actions.features.filters', 'api.features.filters', 'actions.arguments', 'api.mappers'])
  }),
  sorting: Object.freeze({
    key: 'sorting',
    label: 'Sorting',
    description: 'Sort support is missing or does not match provider capabilities.',
    allowedPathPrefixes: Object.freeze(['actions.features.sort', 'api.features.sort', 'actions.arguments'])
  }),
  errorDetection: Object.freeze({
    key: 'errorDetection',
    label: 'Error detection',
    description: 'Provider failures are not detected or surface as false successes.',
    allowedPathPrefixes: Object.freeze(['api.mappers'])
  }),
  actionArguments: Object.freeze({
    key: 'actionArguments',
    label: 'Action arguments',
    description: 'Action arguments are missing, mistyped, or mislocated.',
    allowedPathPrefixes: Object.freeze(['actions.arguments', 'actions.primaryKeyArgument', 'actions.allowBody', 'actions.overwritesAllFields'])
  }),
  responseSchema: Object.freeze({
    key: 'responseSchema',
    label: 'Response schema',
    description: 'The response schema does not match real provider responses.',
    allowedPathPrefixes: Object.freeze(['actions.attributes', 'actions.rawResponse', 'actions.returnsSingleItem', 'api.mappers'])
  }),
  presentation: Object.freeze({
    key: 'presentation',
    label: 'Presentation',
    description: 'Names, descriptions, docs links, or presentation settings are wrong or missing.',
    allowedPathPrefixes: Object.freeze(['actions.presentation', 'actions.description', 'actions.name', 'actions.notes', 'actions.notesVariant', 'actions.documentation', 'description'])
  }),
  lookups: Object.freeze({
    key: 'lookups',
    label: 'Lookups',
    description: 'Lookup arguments point at missing, wrong, or unmappable sources.',
    allowedPathPrefixes: Object.freeze(['actions.arguments'])
  }),
  credentialTestAction: Object.freeze({
    key: 'credentialTestAction',
    label: 'Credential test action',
    description: 'The app lacks a usable credential test action.',
    allowedPathPrefixes: Object.freeze(['credentialTestAction', 'actions'])
  }),
  executionContract: Object.freeze({
    key: 'executionContract',
    label: 'Execution contract',
    description: 'Action execution fails against the live provider for definition reasons.',
    allowedPathPrefixes: Object.freeze(['actions', 'api.features', 'api.mappers', 'api.outboundMappers'])
  }),
  general: Object.freeze({
    key: 'general',
    label: 'General',
    description: 'Anything else the app rules cover, outside credential-flow surfaces.',
    allowedPathPrefixes: Object.freeze(['actions', 'api.features', 'api.mappers', 'api.outboundMappers', 'description', 'credentialTestAction'])
  })
});

const APP_FIX_CATEGORY_KEYS = Object.freeze(Object.keys(appFixCategories));

const getAppFixCategory = (key) => {
  const normalizedKey = String(key || '').trim();
  return appFixCategories[normalizedKey] || null;
};

// 'api[id:abc].features.limit.maxValue' -> 'api.features.limit.maxValue'
const normalizeAppFixDiffPath = (path) => {
  return String(path || '')
    .replace(/\[[^\]]*\]/gu, '')
    .replace(/\.{2,}/gu, '.')
    .replace(/^\.+|\.+$/gu, '');
};

const pathMatchesPrefix = ({ normalizedPath, prefix }) => {
  return normalizedPath === prefix || normalizedPath.startsWith(`${prefix}.`);
};

const isAppFixPathBlocked = (path) => {
  const normalizedPath = normalizeAppFixDiffPath(path);
  if (!normalizedPath) {
    return true;
  }

  return APP_FIX_BLOCKED_PATH_PREFIXES.some((prefix) => pathMatchesPrefix({ normalizedPath, prefix }));
};

const isAppFixPathAllowed = ({ path, category }) => {
  const categoryDefinition = getAppFixCategory(category);
  if (!categoryDefinition) {
    return false;
  }

  if (isAppFixPathBlocked(path)) {
    return false;
  }

  const normalizedPath = normalizeAppFixDiffPath(path);
  return categoryDefinition.allowedPathPrefixes.some((prefix) => pathMatchesPrefix({ normalizedPath, prefix }));
};

const pickHeaderMappers = (outboundMappers) => {
  if (!Array.isArray(outboundMappers)) {
    return [];
  }

  return outboundMappers.filter((mapper) => mapper?.location === 'headers');
};

// Everything that participates in building authenticated requests. Candidate
// definitions must keep this snapshot byte-identical to the published version.
const buildCredentialFlowSnapshot = (definition) => {
  const normalizedDefinition = definition && typeof definition === 'object' ? definition : {};
  const apis = Array.isArray(normalizedDefinition.api) ? normalizedDefinition.api : [];
  const actions = Array.isArray(normalizedDefinition.actions) ? normalizedDefinition.actions : [];

  return {
    authentication: normalizedDefinition.authentication ?? null,
    defaultAuthId: normalizedDefinition.defaultAuthId ?? null,
    activeAppsScreenAuthId: normalizedDefinition.activeAppsScreenAuthId ?? null,
    authTypeCredentials: normalizedDefinition.authTypeCredentials ?? null,
    agentConnection: normalizedDefinition.agentConnection ?? null,
    hooks: normalizedDefinition.hooks ?? null,
    baseUrl: normalizedDefinition.baseUrl ?? null,
    api: apis.map((api) => ({
      id: api?.id ?? null,
      type: api?.type ?? null,
      baseUrl: api?.baseUrl ?? null,
      headers: api?.headers ?? null,
      headerMappers: pickHeaderMappers(api?.outboundMappers)
    })),
    actions: actions.map((action) => ({
      id: action?.id ?? null,
      headers: action?.headers ?? null,
      oauth: action?.oauth ?? null,
      hooks: action?.hooks ?? null,
      headerMappers: pickHeaderMappers(action?.outboundMappers),
      // The action-level api block (request URL/type/headers) is credential-flow
      // critical too; capturing it here backs up the path-block layer.
      api: action?.api ? { baseUrl: action.api.baseUrl ?? null, type: action.api.type ?? null, headers: action.api.headers ?? null } : null
    }))
  };
};

const credentialFlowMatches = ({ candidateDefinition, publishedDefinition }) => {
  return JSON.stringify(buildCredentialFlowSnapshot(candidateDefinition)) === JSON.stringify(buildCredentialFlowSnapshot(publishedDefinition));
};

export { APP_FIX_BLOCKED_PATH_PREFIXES, APP_FIX_CATEGORY_KEYS, appFixCategories, buildCredentialFlowSnapshot, credentialFlowMatches, getAppFixCategory, isAppFixPathAllowed, isAppFixPathBlocked, normalizeAppFixDiffPath };
